// ============================================================================
//  /app  —  single QR destination that routes customers to the right store.
//
//  QR codes printed in-store point permanently to  https://matchastory.my/app
//  Change APPLE_APP_URL / GOOGLE_PLAY_URL below when the store listings move;
//  the printed QRs do NOT need to be reprinted.
// ============================================================================

import { useEffect, useState } from "react";
import { Apple } from "lucide-react";

// --- Store URLs (EDIT HERE ONLY) ---------------------------------------------
const APPLE_APP_URL  = "https://apps.apple.com/us/app/matcha-story-my/id6813346685";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=my.matchastory.app";
// -----------------------------------------------------------------------------


/** Detect the user's platform from the User-Agent string.
 *  Returns "ios" | "android" | "other".
 *
 *  iPadOS 13+ masquerades as desktop Mac Safari, so we also check
 *  `maxTouchPoints` to catch it. */
function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = (navigator.userAgent || navigator.vendor || "").toLowerCase();

  // Android (phones + tablets)
  if (/android/.test(ua)) return "android";

  // Classic iOS devices
  if (/iphone|ipad|ipod/.test(ua)) return "ios";

  // iPadOS 13+ (reports as "Macintosh") — detect via touch points
  if (/mac/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1) {
    return "ios";
  }

  return "other";
}


export default function AppDownload() {
  // `null` while detection is in flight so the manual-choice buttons never
  // flash on mobile devices during the redirect.
  const [platform, setPlatform] = useState(null);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    if (p === "ios") {
      window.location.replace(APPLE_APP_URL);
    } else if (p === "android") {
      window.location.replace(GOOGLE_PLAY_URL);
    }
  }, []);

  const isMobile = platform === "ios" || platform === "android";

  return (
    <div
      className="min-h-screen bg-bone text-ink flex flex-col"
      style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}
      data-testid="app-download-root"
    >
      {/* Minimal top bar — just the wordmark, no nav links */}
      <header className="px-5 pt-6 pb-2">
        <a href="/" aria-label="Matcha Story home" className="inline-flex items-center" data-testid="app-logo">
          <span className="font-display text-base md:text-lg tracking-widest text-ink">
            MATCHA<span className="text-matcha-dark">·</span>STORY
          </span>
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-md text-center">

          {/* Matcha Story app icon */}
          <img
            src="/matcha-story-icon.png"
            alt="Matcha Story"
            width="112"
            height="112"
            className="mx-auto mb-8 w-28 h-28 rounded-[22%] shadow-lg"
            data-testid="app-icon"
          />

          <h1
            className="font-display text-3xl sm:text-4xl md:text-5xl text-ink leading-tight tracking-wide"
            data-testid="app-headline"
          >
            DOWNLOAD THE<br />MATCHA STORY APP
          </h1>

          <p
            className="mt-5 text-base sm:text-lg text-ink/70 leading-relaxed"
            data-testid="app-subheadline"
          >
            Order easier. Earn points.<br />Enjoy more discounts &amp; perks.
          </p>

          {/* Mobile: show a tasteful "redirecting" indicator while the UA kicks us to the store */}
          {isMobile && (
            <div className="mt-10" data-testid="app-redirecting">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-matcha-dark">
                <span className="w-2 h-2 rounded-full bg-matcha-dark animate-pulse" />
                Opening the {platform === "ios" ? "App Store" : "Google Play"}…
              </div>
              <p className="text-[11px] text-ink/50 mt-3">
                Not redirected?{" "}
                <a
                  href={platform === "ios" ? APPLE_APP_URL : GOOGLE_PLAY_URL}
                  className="underline underline-offset-2 text-matcha-dark"
                  data-testid="app-manual-link"
                >
                  Tap here
                </a>.
              </p>
            </div>
          )}

          {/* Desktop / unknown / detection failed → show both buttons */}
          {platform === "other" && (
            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3" data-testid="app-store-buttons">
              <a
                href={APPLE_APP_URL}
                data-testid="app-apple-btn"
                className="inline-flex items-center justify-center gap-3 px-6 py-3.5 bg-ink text-bone rounded-full hover:opacity-90 transition-opacity"
              >
                <Apple size={22} strokeWidth={1.5} />
                <span className="text-left leading-tight">
                  <span className="block text-[10px] uppercase tracking-[0.2em] opacity-70">Download on the</span>
                  <span className="block text-base font-medium">App Store</span>
                </span>
              </a>

              <a
                href={GOOGLE_PLAY_URL}
                data-testid="app-google-btn"
                className="inline-flex items-center justify-center gap-3 px-6 py-3.5 bg-ink text-bone rounded-full hover:opacity-90 transition-opacity"
              >
                {/* Inline Google Play glyph */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M3.6 2.3c-.3.3-.4.7-.4 1.2v17c0 .5.1.9.4 1.2l.1.1 9.5-9.5v-.2L3.6 2.3z" fill="currentColor" opacity="0.9"/>
                  <path d="M16.4 15.5l-3.2-3.2v-.2l3.2-3.2.1.1 3.8 2.1c1.1.6 1.1 1.6 0 2.2l-3.9 2.2z" fill="currentColor"/>
                  <path d="M16.5 15.4l-3.3-3.3L3.6 21.7c.4.4 1 .4 1.7.1l11.2-6.4z" fill="currentColor" opacity="0.75"/>
                  <path d="M16.5 8.6L5.3 2.2c-.7-.4-1.3-.3-1.7.1l9.6 9.6 3.3-3.3z" fill="currentColor" opacity="0.6"/>
                </svg>
                <span className="text-left leading-tight">
                  <span className="block text-[10px] uppercase tracking-[0.2em] opacity-70">Get it on</span>
                  <span className="block text-base font-medium">Google Play</span>
                </span>
              </a>
            </div>
          )}

          {/* Keep the layout stable while detection runs (first paint) */}
          {platform === null && (
            <div className="mt-10 h-14" aria-hidden="true" />
          )}
        </div>
      </main>

      <footer className="px-5 pb-6 text-center text-[10px] uppercase tracking-[0.22em] text-ink/40">
        © Matcha Story · Freshly made, every day
      </footer>
    </div>
  );
}
